import {Platform, ScrollView} from 'react-native';
import { themeColorSet, ThemedPressable, ThemedText, ThemedView, themeTextVariants } from '@app/theme';
import Button from '@app/sharedComponents/Button/Button';
import CommonHeader from '@app/sharedComponents/CommonHeader/CommonHeader';
import { useEffect, useState } from 'react';
import { PrivateStackParamList } from '@app/navigationApp/types';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import ROUTE from '@app/navigationApp/routes';
import { formatIndianCurrency } from '@app/utils/utility';
import dayjs from 'dayjs';
import { Info } from '@app/sharedComponents/Icons/Icons';
import axios from 'axios';
import { endpoints } from '@app/api/apis';
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet';
import VerificationOTP from '@app/sharedComponents/VerificationOTP/VerificationOTP';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import FlexibleInfo from './components/FlexibleInfo';
import AccountOpeningInfo from './components/AccountOpeningInfo';
import UpdatedOutstanding from './components/UpdatedOutstanding';
import InterestInfo from './components/InterestInfo';
import MonthlyInterest from './components/MonthlyInterest';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import handleApiErr from '@app/helper/handleApiErr';
import ChargesInfo from './components/ChargesInfo';
import useUserProfile from '@app/hooks/useUserProfile';
import PageLoader from '@app/sharedComponents/PageLoader/PageLoader';

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'CONFIRM_WITHDRAW_AMOUNT'>;

const ConfirmWithdrawAmount = ({navigation, route} : ScreenProps)=>{
    const dispatch = useAppDispatch()
    const userDetails = useAppSelector((state) => state.auth.userDetails);
    const { userProfile, isLoading: isUserProfileLoading } = useUserProfile();
    const bankDetails = userProfile?.bankDetails;
    const accountNumber = bankDetails?.accountNumber;
    const bankName = bankDetails?.bankName;
    const withdrawDetails = route?.params?.withdrawDetails;
    const {lienMarkingFee,annualInterestRate,accountOpeningFee,netDisbursalAmount,withdrawalAmount,dailyEMI,nextEMIDate,interestAccuredNotDue,estimatedInterestTillEndOfCycle,expectedTransferDate,totalPrincipalOutStanding,currentOutStanding,remark,nextEMIAmount,monthlyInterestEMI,monthlyEMIRate,creditId,processingFee,gstOnProcessingFee,stampDutyFee} = withdrawDetails;
    const [buttonLoader, setButtonLoader] = useState(false);
    const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
    const [bottomSheetType, setBottomSheetType] = useState(null);
    const [isNotFirstDisbursal, setIsNotFirstDisbursal] = useState(false);

    const fetchDisbursalDetails = () => {
        const url = endpoints.disbursalDetails + creditId
        axios.get(url)
        .then((res)=>{
            console.log('disbursal details',res.data)
            setIsNotFirstDisbursal(res.data?.length>0)
        }
        ).catch((err)=>{
            console.log('disbursal details',err)
        }
        )
    }

    useEffect(() => {
        fetchDisbursalDetails();
    },[])

    const onVerificationSuccess = (data:any) => {
        setButtonLoader(true);
        setTimeout(() => {
          setButtonLoader(false);
          navigation.navigate(ROUTE.WITHDRAW_SUCCESS,{successData:data,isNotFirstDisbursal});
        }, 700);
    };

    const openBottomSheet = (type:string) => {
        setBottomSheetType(type);
        setIsBottomSheetVisible(true);
    };

    const closeBottomSheet = () => {
        if(Platform.OS === 'web') {
        setTimeout(()=>{setBottomSheetType(null)},500)
        }
        setIsBottomSheetVisible(false);
    };

    const requestWithdrawOTP = () => {
        setButtonLoader(true);
        const payload ={
            creditId,
            disbursalAmount:withdrawalAmount
        }
        axios.post(endpoints.requestWithdrwalOtp, payload)
        .then((res)=>{
            openBottomSheet('OTP')
            console.log('otp',res.data)
        }
        ).catch((err)=>{
            if(handleApiErr(err)){
                dispatch(showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: err?.data?.message,
                    closeOnBackdropPress: true,
                    errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                    showErrorHeading: true,
                    errorHeading: err?.data?.header,
                    btnName:'Go back',
                }))
            }
            console.log('otp',err)
        }).finally(()=>{
            setButtonLoader(false);
        })
    }

    if (isUserProfileLoading) {
        return <PageLoader/>;
    }

    return(
    <>
        <CommonHeader navigation={navigation} pageName='CONFIRM_WITHDRAW_AMOUNT' label="Confirm amount"/>
        <ScrollView contentContainerStyle={{flexGrow: 1,backgroundColor:themeColorSet['bg-surface-page']}}>
            <ThemedView 
                backgroundColor='bg-surface-primary'
                borderColor='border-primary'
                borderTopWidth={1}
                borderBottomWidth={1}
                mt="lg"
                mb="md"
                p='lg'>
                <ThemedText variant='b3r' color='text-secondary' mb='xs'>
                    Withdrawal amount
                </ThemedText>
                <ThemedText variant='l1' color='text-primary'>
                    ₹{formatIndianCurrency(withdrawalAmount)}
                </ThemedText>
                {accountOpeningFee > 0 &&
                <ThemedView backgroundColor='bg-fill-secondary' p='md' borderRadius='sm' my='lg'>
                    <ThemedView flexDirection='row' justifyContent='space-between' alignItems='center'>
                        <ThemedView flexDirection='row' alignItems='center'>
                        <ThemedText variant='b3r' color='text-primary'>{isNotFirstDisbursal ? "Outstanding charges ":"Account opening fee(incl. GST) " }</ThemedText>
                        <ThemedPressable hitSlop={{top : 10 , left :10 , right : 10 ,bottom : 10}} onPress={()=>openBottomSheet(isNotFirstDisbursal ? "CHARGESINFO" : "ACCOUNTOPENINFO")}  mt="2xs">
                            <Info size={16}/>
                        </ThemedPressable>
                        </ThemedView>
                        <ThemedText variant='b3r' color='text-primary'>₹{formatIndianCurrency(accountOpeningFee)}</ThemedText>
                    </ThemedView>
                    <ThemedView height={1} backgroundColor='border-primary' width={'100%'} my='lg' />
                    <ThemedView  flexDirection='row' justifyContent='space-between' alignItems='center'>
                        <ThemedText variant='b3b' color='text-primary'>Net disbursal amount</ThemedText>
                        <ThemedText variant='b3b' color='text-emphasis-secondary'>₹{formatIndianCurrency(netDisbursalAmount)}</ThemedText>
                    </ThemedView>
                </ThemedView>
                }
            </ThemedView>
            <ThemedView 
                backgroundColor='bg-surface-primary'
                borderColor='border-primary'
                borderTopWidth={1}
                borderBottomWidth={1}
                mb="sm"
                p='lg'>
                <ThemedView flexDirection='row' justifyContent='space-between' alignItems='center' >
                    <ThemedText variant='b3r' color='text-secondary' >
                        Transfer to
                    </ThemedText>
                    <ThemedText variant='b3r' color='text-primary' >
                        {bankName} xxxx {accountNumber?.toString().slice(-4)}
                    </ThemedText>
                </ThemedView>

                <ThemedView height={1} backgroundColor='border-primary' width={'100%'} my='lg' />
                <ThemedView flexDirection='row' justifyContent='space-between' alignItems='center' mb="sm">
                    <ThemedText variant='b3r' color='text-secondary' >
                        Expected transfer by
                    </ThemedText>
                    <ThemedText variant='b3r' color='text-primary' >
                       {dayjs(expectedTransferDate).format('h:mm A, DD-MMM')}
                    </ThemedText>
                </ThemedView>
                {remark && <ThemedView backgroundColor='bg-fill-warning' p='md' borderRadius='sm' >
                    <ThemedText variant='b4r' color='text-primary' >{remark}</ThemedText>
                </ThemedView>}
            </ThemedView>
            <ThemedView 
                backgroundColor='bg-surface-primary'
                borderColor='border-primary'
                borderTopWidth={1}
                borderBottomWidth={1}
                mb="md"
                p='lg'>
                <ThemedText variant='b3b' color='text-primary' mb='lg' >Updated repayment details</ThemedText>
                <ThemedView flexDirection='row' justifyContent='space-between' alignItems='center' >
                    <ThemedView flexDirection='row' alignItems='center'>
                    <ThemedText  variant='b3r' color='text-primary'>Updated principal outstanding </ThemedText>
                    <ThemedPressable hitSlop={{top : 10 , left :10 , right : 10 ,bottom : 10}} onPress={()=>openBottomSheet("PRINCIPALOUTSTANDING")} mt="2xs">
                        <Info size={16}/>
                    </ThemedPressable>
                    </ThemedView>
                    <ThemedText  variant='b3r' color='text-primary'>₹{formatIndianCurrency(totalPrincipalOutStanding)}</ThemedText>
                </ThemedView>
                <ThemedView height={1} backgroundColor='border-primary' width={'100%'} my='lg' />
                <ThemedView>
                    <ThemedView  flexDirection='row' justifyContent='space-between' alignItems='center'>
                        <ThemedText  variant='b3r' color='text-primary'>Estimated interest due</ThemedText>
                        <ThemedText variant='b3r' color='text-primary'>₹{formatIndianCurrency(nextEMIAmount)}</ThemedText>
                    </ThemedView>
                    <ThemedPressable onPress={()=>openBottomSheet("INTERESTINFO")}>
                    <ThemedText  variant='b4b' color='text-action-primary'>See calculation</ThemedText>
                    </ThemedPressable>
                </ThemedView>
                <ThemedView height={1} backgroundColor='border-primary' width={'100%'} my='lg' />
                <ThemedView flexDirection='row' justifyContent='space-between' alignItems='center' >
                    <ThemedText  variant='b3r' color='text-primary'>Interest due date</ThemedText>
                    <ThemedText  variant='b3r' color='text-primary'>{nextEMIDate} </ThemedText>
                </ThemedView>
                <ThemedView height={1} backgroundColor='border-primary' width={'100%'} my='lg' />
                <ThemedView>
                    <ThemedView  flexDirection='row' justifyContent='space-between' alignItems='center'>
                        <ThemedText  variant='b3r' color='text-primary'>Monthly interest (approx.)</ThemedText>
                        <ThemedText variant='b3r' color='text-primary'>₹{formatIndianCurrency(monthlyInterestEMI)}</ThemedText>
                    </ThemedView>
                    <ThemedPressable onPress={()=>openBottomSheet("MONTHLYINTEREST")}>
                        <ThemedText  variant='b4b' color='text-action-primary'>See calculation</ThemedText>
                    </ThemedPressable>
                </ThemedView>
                <ThemedView height={1} backgroundColor='border-primary' width={'100%'} my='lg' />
                <ThemedView flexDirection='row' justifyContent='space-between' alignItems='center' >
                    <ThemedText  variant='b3r' color='text-primary'>Principal repayment schedule</ThemedText>
                    <ThemedView flexDirection='row' alignItems='center'>
                    <ThemedText  variant='b3r' color='text-primary'>Flexible </ThemedText>
                    <ThemedPressable hitSlop={{top : 10 , left :10 , right : 10 ,bottom : 10}} onPress={()=>openBottomSheet("FLEXIBLE")}  mt="2xs">
                        <Info size={16}/>
                    </ThemedPressable>
                    </ThemedView>
                </ThemedView>
            </ThemedView>
        </ScrollView>
        <ThemedView backgroundColor='bg-fill-primary'  p="lg" >
          <Button
            eventName={"WITHDRAW_CONFIRM_BUTTON"}
            label={'Withdraw ₹'+ formatIndianCurrency(withdrawalAmount)}
            handlePress={requestWithdrawOTP}
            type={'SOLID'}
            size='MD'
            buttonLoader={buttonLoader}
          />
        </ThemedView>
        <BottomSheetContainer
            isVisible={isBottomSheetVisible}
            close={closeBottomSheet}
            closeOnBackdropPress={true}
            borderTopRightRadius={10}
            onClose={closeBottomSheet}
            borderTopLeftRadius={10}
            containerStyle={{ backgroundColor: '#fefefe' }}
            showCloseIcon
        >
        {
            bottomSheetType ==='MONTHLYINTEREST' && 
        <MonthlyInterest
            openPrinicpalCard={()=>openBottomSheet('PRINCIPALOUTSTANDING')}
            closeBottomSheet={closeBottomSheet}
            totalPrincipalOutStanding={totalPrincipalOutStanding}
            dailyEMI={dailyEMI}
            monthlyInterestEMI={monthlyInterestEMI}
            annualInterestRate={annualInterestRate}
        />
        }
        {
        bottomSheetType ==='INTERESTINFO' && 
        <InterestInfo 
            closeBottomSheet={closeBottomSheet}
            estimatedInterestTillEndOfCycle={estimatedInterestTillEndOfCycle}
            nextEMIAmount={nextEMIAmount}
            interestAccuredNotDue={interestAccuredNotDue}
        />
        }
        {
        bottomSheetType ==='PRINCIPALOUTSTANDING' &&
        <UpdatedOutstanding
            closeBottomSheet={closeBottomSheet}
            currentOutStanding={currentOutStanding}
            totalPrincipalOutStanding={totalPrincipalOutStanding}
            withdrawalAmount={withdrawalAmount}
        />
        }
        {bottomSheetType ==='ACCOUNTOPENINFO' && 
        <AccountOpeningInfo 
            accountOpeningFee={accountOpeningFee}
            closeBottomSheet={closeBottomSheet}
            processingFee={processingFee}
            gstOnProcessingFee={gstOnProcessingFee}
            stampDutyFee={stampDutyFee}
            lienMarkingFee={lienMarkingFee}
        />}
        {bottomSheetType ==='CHARGESINFO' && 
        <ChargesInfo 
            accountOpeningFee={accountOpeningFee}
            closeBottomSheet={closeBottomSheet}
            navigation={navigation}
        />}
        {bottomSheetType ==='FLEXIBLE' && <FlexibleInfo monthlyEMIRate={monthlyEMIRate} closeBottomSheet={closeBottomSheet} />}
        {bottomSheetType ==='OTP' &&
         <VerificationOTP 
            otpType="PHONE_NUMBER"
            otpSentTo={userDetails?.user?.phoneNumber}
            resendUrl={endpoints?.requestWithdrwalOtp}
            apiPayload={{creditId,disbursalAmount:withdrawalAmount}}
            verifyUrl={endpoints?.verifyWithdrawlOtp}
            otpDigitCount={6}
            attributeKeyToVerify={'phoneNo'}
            attributeValueToVerify={userDetails?.user?.phoneNumber}
            currentStep='WITHDRAW'
            uniqueIdentifier={creditId}
            onClose={closeBottomSheet}
            onVerificationSuccess={onVerificationSuccess}
        />}
        </BottomSheetContainer>
    </>
    )
}

export default ConfirmWithdrawAmount;